import axios from "axios";
import { BASE_URL } from "../_constants/urlConstants";
import { EQUIPMENT } from "../_constants/actionConstants";
import raiseError from "./raiseError";

/**
 * Normalizes a ProjectEquipmentListDto into the backward-compatible shape used
 * by all existing consumers (ADR, reports, search modal, etc.).
 * Aliases projectEquipmentId → equipmentId so existing code does not need updating.
 * @param {Object} pe - ProjectEquipmentListDto from /api/ProjectEquipment/*
 */
const toEquipState = (pe) => ({
  ...pe,
  realEquipmentId: pe.equipmentId,
  equipmentId: pe.projectEquipmentId,
  number: pe.equipmentNumber,
  make: pe.equipmentMake || "",
  model: pe.equipmentModel || "",
  description: pe.description || "",
  equipClass: pe.equipmentClass || "",
  numberMakeDesc: pe.numberDesc || "",
  makeDesc:
    [pe.equipmentMake, pe.equipmentModel].filter(Boolean).join(" ") ||
    pe.equipmentModelDescription ||
    pe.description ||
    "",
});

const getAll = (projectId) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.FETCH_ALL });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/List/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: EQUIPMENT.FETCH_ALL_FULFILLED,
      projEquipList: res.data.map(toEquipState),
      projectId,
    });
    return res.data;
  } catch (err) {
    raiseError(EQUIPMENT.FETCH_ALL_REJECTED, err.response.data);
  }
};

/**
 * Same as getAll, but includes merged-away (inactive) equipment — so a historical daily
 * report can still resolve the Number/Description of equipment it referenced before a
 * later merge (ADR review page, PDF export). Read-only use only: do not use this list to
 * populate a selection dropdown or a duplicate-number check, since a merged record must
 * not reappear as choosable there.
 */
const getAllIncludingInactive = (projectId) => async (dispatch, getState) => {
  const existing = getState().equipment.projEquipListsIncludingInactive[projectId];
  if (existing && existing.length > 0) return existing;

  dispatch({ type: EQUIPMENT.FETCH_ALL_INCLUDING_INACTIVE });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/ListIncludingInactive/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: EQUIPMENT.FETCH_ALL_INCLUDING_INACTIVE_FULFILLED,
      projEquipListIncludingInactive: res.data.map(toEquipState),
      projectId,
    });
    return res.data;
  } catch (err) {
    raiseError(EQUIPMENT.FETCH_ALL_INCLUDING_INACTIVE_REJECTED, err.response.data);
  }
};

/**
 * Normalizes a ProjectEquipmentListDto for the "All Available" browse view, where many
 * rows may not be linked to the project yet (ProjectEquipmentId is the 0 sentinel for
 * those). Unlike toEquipState, this keeps `equipmentId` as the real, always-unique
 * Equipment.EquipmentId rather than aliasing it to projectEquipmentId — aliasing to
 * projectEquipmentId would collapse every unlinked row's id to 0.
 */
const toAvailableEquipState = (pe) => ({
  ...pe,
  isLinked: pe.projectEquipmentId > 0,
  number: pe.equipmentNumber,
  make: pe.equipmentMake || "",
  model: pe.equipmentModel || "",
  description: pe.description || "",
  equipClass: pe.equipmentClass || "",
  numberMakeDesc: pe.numberDesc || "",
  makeDesc:
    [pe.equipmentMake, pe.equipmentModel].filter(Boolean).join(" ") ||
    pe.equipmentModelDescription ||
    pe.description ||
    "",
});

/**
 * Loads every Equipment record belonging to any contractor on this project, linked or
 * not — a pure read, does not create any ProjectEquipment links.
 */
const getAllAvailable = (projectId) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.FETCH_ALL_AVAILABLE });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/AllAvailable/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: EQUIPMENT.FETCH_ALL_AVAILABLE_FULFILLED,
      allAvailableEquipList: res.data.map(toAvailableEquipState),
      projectId,
    });
    return res.data;
  } catch (err) {
    raiseError(EQUIPMENT.FETCH_ALL_AVAILABLE_REJECTED, err.response.data);
  }
};

/**
 * Explicitly links a catalog Equipment record to the project. On success, the item
 * moves from the "All Available" list into the "In Project" list.
 */
const linkToProject = (projectId, contractorId, equipmentId) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.LINK_TO_PROJECT });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/LinkToProject`;
  try {
    const res = await axios.post(apiEndpoint, { projectId, contractorId, equipmentId });
    const normalized = toEquipState(res.data);
    dispatch({
      type: EQUIPMENT.LINK_TO_PROJECT_FULFILLED,
      projectId,
      equipmentId,
      equipment: normalized,
    });
    return normalized;
  } catch (err) {
    raiseError(EQUIPMENT.LINK_TO_PROJECT_REJECTED, err.response.data);
  }
};

/**
 * Adds a manual-entry ProjectEquipment record.
 * Accepts the old EquipmentDto field shape (number, make, model, equipClass, description)
 * and maps it to the new ProjectEquipmentListDto.
 * Also accepts catalog fields directly (equipmentClass, equipmentMake, etc.)
 * to create a catalog-linked entry in one call.
 */
const add = (newEquipData) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.ADD });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/ManualEntry`;

  const dto = {
    projectId: newEquipData.projectId,
    contractorId: newEquipData.contractorId,
    equipmentNumber: newEquipData.equipmentNumber || newEquipData.number || "",
    description: newEquipData.description || "",
    isRental: newEquipData.isRental || false,
    isManualEntry: !!newEquipData.isManualEntry,
    // Catalog fields (optional — omit for pure manual entries)
    equipmentClass: newEquipData.equipmentClass || null,
    equipmentMake: newEquipData.equipmentMake || null,
    equipmentModel: newEquipData.equipmentModel || null,
    equipmentClassDescription: newEquipData.equipmentClassDescription || null,
    equipmentMakeDescription: newEquipData.equipmentMakeDescription || null,
    equipmentModelDescription: newEquipData.equipmentModelDescription || null,
    equipmentType: newEquipData.equipmentType || null,
    rate: newEquipData.rate ?? null,
    delayFactor: newEquipData.delayFactor ?? null,
    otFactor: newEquipData.otFactor ?? null,
  };

  try {
    const res = await axios.post(apiEndpoint, dto);
    const normalized = toEquipState(res.data);
    dispatch({ type: EQUIPMENT.ADD_FULFILLED, projectId: newEquipData.projectId, equipment: normalized });
    return normalized;
  } catch (err) {
    raiseError(EQUIPMENT.ADD_REJECTED, err.response.data);
  }
};

const update = (equipData) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.EDIT });
  const projectEquipmentId =
    equipData.projectEquipmentId || equipData.equipmentId;
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/${projectEquipmentId}`;

  const dto = {
    projectId: equipData.projectId,
    contractorId: equipData.contractorId,
    projectEquipmentId,
    equipmentNumber:
      equipData.equipmentNumber || equipData.number || "",
    description: equipData.description || "",
    equipmentClass: equipData.equipmentClass || equipData.equipClass || null,
    equipmentMake: equipData.equipmentMake || equipData.make || null,
    equipmentModel: equipData.equipmentModel || equipData.model || null,
    equipmentClassDescription: equipData.equipmentClassDescription || null,
    equipmentMakeDescription: equipData.equipmentMakeDescription || null,
    equipmentModelDescription: equipData.equipmentModelDescription || null,
    equipmentType: equipData.equipmentType || null,
    isRental: equipData.isRental || false,
    isManualEntry: equipData.isManualEntry !== undefined ? equipData.isManualEntry : true,
    rate: equipData.rate ?? null,
    delayFactor: equipData.delayFactor ?? null,
    otFactor: equipData.otFactor ?? null,
  };

  try {
    const res = await axios.put(apiEndpoint, dto);
    const normalized = toEquipState(res.data);
    dispatch({
      type: EQUIPMENT.EDIT_FULFILLED,
      projectId: equipData.projectId,
      equipmentId: projectEquipmentId,
      equipmentDetails: normalized,
    });
    return normalized;
  } catch (err) {
    raiseError(EQUIPMENT.EDIT_REJECTED, err.response.data);
  }
};

/**
 * Ensures ProjectEquipment rows exist for all non-manual catalog equipment belonging to the
 * contractor, then loads them into the separate contractorCatalogEquip Redux slice so the
 * ADR equipment dropdown is fully populated without contaminating the Project Admin list.
 * No-ops if the catalog for this contractor is already loaded in Redux.
 */
const getForContractor = (projectId, contractorId) => async (dispatch, getState) => {
  const existing = getState().equipment.contractorCatalogEquip[contractorId];
  if (existing && existing.length > 0) return;

  dispatch({ type: EQUIPMENT.FETCH_CONTRACTOR_CATALOG, contractorId });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/ContractorCatalog/${projectId}/${contractorId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: EQUIPMENT.FETCH_CONTRACTOR_CATALOG_FULFILLED,
      contractorEquipList: res.data.map(toEquipState),
      contractorId,
    });
  } catch (err) {
    // Non-fatal: ADR dropdown falls back to previously loaded project equipment
    dispatch({ type: EQUIPMENT.FETCH_CONTRACTOR_CATALOG_REJECTED, contractorId });
  }
};

const mergeEquipment = (sourceProjectEquipmentId, targetProjectEquipmentId, projectId, contractorId) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.MERGE });
  try {
    const res = await axios.post(`${BASE_URL}/api/ProjectEquipment/Merge`, {
      sourceProjectEquipmentId,
      targetProjectEquipmentId,
    });
    dispatch({ type: EQUIPMENT.MERGE_FULFILLED, sourceProjectEquipmentId, contractorId });
    dispatch(getAll(projectId));
    return res.data;
  } catch (err) {
    dispatch({ type: EQUIPMENT.MERGE_REJECTED });
    dispatch(getAll(projectId));
    throw err;
  }
};

const getNumberChangeImpact = (projectEquipmentId) => async () => {
  const res = await axios.get(
    `${BASE_URL}/api/ProjectEquipment/${projectEquipmentId}/NumberChangeImpact`
  );
  return res.data;
};

/**
 * Bulk-uploads catalog-linked equipment from parsed CSV rows.
 * @param {Object} request - { projectId, contractorId, equipmentType, reportDate, rows }
 * Returns { successCount, rows: [{ rowNumber, equipmentNumber, success, errorMessage }] }.
 */
const uploadCsv = (request) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.GENERIC_REQUEST });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/UploadCsv`;
  try {
    const res = await axios.post(apiEndpoint, request);
    dispatch({ type: EQUIPMENT.GENERIC_REQUEST_FULFILLED });
    if (res.data?.successCount > 0) dispatch(getAll(request.projectId));
    return res.data;
  } catch (err) {
    raiseError(EQUIPMENT.GENERIC_REQUEST_REJECTED, err.response.data);
    throw err;
  }
};

/**
 * Downloads the project's equipment list as a CSV file, with catalog rates resolved
 * for today's date.
 */
const downloadCsv = (projectId) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.GENERIC_REQUEST });
  const reportDate = new Date().toISOString().split("T")[0];
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/${projectId}/Csv?reportDate=${reportDate}`;
  try {
    const response = await axios.get(apiEndpoint, { responseType: "blob" });
    dispatch({ type: EQUIPMENT.GENERIC_REQUEST_FULFILLED });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `equipment_${projectId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    raiseError(EQUIPMENT.GENERIC_REQUEST_REJECTED, err.response.data);
  }
};

const deleteEquipment = (equipmentId, projectId, contractorId) => async (dispatch) => {
  dispatch({ type: EQUIPMENT.DELETE });
  const apiEndpoint = `${BASE_URL}/api/ProjectEquipment/${equipmentId}?projectId=${projectId}`;
  try {
    await axios.delete(apiEndpoint);
    dispatch({ type: EQUIPMENT.DELETE_FULFILLED, equipmentId, projectId, contractorId });
    dispatch({ type: EQUIPMENT.CLEAR_DETAILS, equipmentId, projectId });
  } catch (err) {
    raiseError(EQUIPMENT.DELETE_REJECTED, err.response.data);
  }
};

// Keep this interface alphabetical
const equipmentActions = {
  add,
  deleteEquipment,
  downloadCsv,
  getAll,
  getAllAvailable,
  getAllIncludingInactive,
  getForContractor,
  getNumberChangeImpact,
  linkToProject,
  mergeEquipment,
  update,
  uploadCsv,
};

export default equipmentActions;
